/**
 * Estado de quests fijadas (pin) + favorita, persistido en localStorage por
 * personaje. Espeja el patrón de `MapMarkers.ts`.
 *
 * - `pinnedIds`: quests fijadas (orden de pineo), máx 3 → aparecen en la ventana
 *   flotante `QuestPinnedPanel`.
 * - `favoritedId`: única favorita (estrella) → destino de la ruta del World Map.
 * - `collapsedIds`: quests con su lista de objetivos colapsada en el panel.
 * - `panelX`/`panelY`: posición arrastrada del panel, relativa al canvas (px).
 *   `null` = sin posición guardada (arranca en la esquina por defecto).
 */

export const MAX_PINNED_QUESTS = 3;

export interface QuestPinsState {
  pinnedIds: string[];
  favoritedId: string | null;
  collapsedIds: string[];
  panelX: number | null;
  panelY: number | null;
}

const STORAGE_PREFIX = 'aureum_quest_pins_v1_';

export function emptyQuestPinsState(): QuestPinsState {
  return { pinnedIds: [], favoritedId: null, collapsedIds: [], panelX: null, panelY: null };
}

export function loadQuestPins(characterId: string): QuestPinsState {
  if (!characterId || typeof window === 'undefined') return emptyQuestPinsState();
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + characterId);
    if (!raw) return emptyQuestPinsState();
    const parsed = JSON.parse(raw) as Partial<QuestPinsState>;
    if (!parsed || typeof parsed !== 'object') return emptyQuestPinsState();
    // Saneo defensivo: strings en los arrays, favorita string|null, coords number|null.
    const pinnedIds = Array.isArray(parsed.pinnedIds)
      ? parsed.pinnedIds.filter((x): x is string => typeof x === 'string').slice(0, MAX_PINNED_QUESTS)
      : [];
    const collapsedIds = Array.isArray(parsed.collapsedIds)
      ? parsed.collapsedIds.filter((x): x is string => typeof x === 'string')
      : [];
    const favoritedId = typeof parsed.favoritedId === 'string' ? parsed.favoritedId : null;
    const panelX = typeof parsed.panelX === 'number' ? parsed.panelX : null;
    const panelY = typeof parsed.panelY === 'number' ? parsed.panelY : null;
    return { pinnedIds, favoritedId, collapsedIds, panelX, panelY };
  } catch {
    return emptyQuestPinsState();
  }
}

export function saveQuestPins(characterId: string, state: QuestPinsState): void {
  if (!characterId || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_PREFIX + characterId, JSON.stringify(state));
  } catch {
    // Ignorar QuotaExceededError / acceso denegado.
  }
}
